import React, { useState, useEffect, useCallback, useRef } from 'react';
import ErpiaSelect from './ErpiaSelect';
import APIService from '../services/api';
import CvEditeur from './CvEditeur';
import CVPrep from './CVPrep';

/**
 * Lanceur de l'éditeur de CV, à ouvrir depuis n'importe quel aperçu de CV.
 *
 * L'éditeur travaille sur les sections structurées d'une préparation (cv-prep),
 * jamais sur un fichier. Partout où l'on voit un CV, on ne connaît en revanche
 * qu'un document — il faut donc retrouver la préparation correspondante, dans
 * cet ordre :
 *
 *   1. la préparation dont le document généré EST celui qu'on regarde ;
 *   2. celle du besoin en cours, quand l'écran en désigne un ;
 *   3. la seule qui existe, s'il n'y en a qu'une ;
 *   4. sinon on demande — deux préparations pour deux besoins ne se valent pas.
 *
 * Aucune préparation : si un CV déposé existe, on l'ouvre DIRECTEMENT — sa
 * version modifiable est créée par reprise fidèle du contenu (mode import),
 * sans écran intermédiaire, comme on ouvre un document dans Word. Quand
 * l'écran appelant ne précise pas le document, on retrouve le CV déposé sur
 * la fiche. Le besoin est FACULTATIF : éditer un CV ne demande pas d'être
 * positionné — seuls le score et l'optimisation ciblée en ont besoin, et ils
 * s'ajoutent quand on rattache un besoin. L'écran manuel (CV vierge,
 * pré-remplissage) n'est qu'un repli : reprise impossible, ou aucun fichier.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });

const gBtn = {
  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 11px', borderRadius: '8px',
  border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)',
  fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
};

const STATUTS = {
  DRAFT: 'Brouillon',
  SENT_TO_CANDIDATE: 'Envoyé candidat',
  VALIDATED_BY_CANDIDATE: 'Validé candidat',
  READY_FOR_SUBMISSION: 'Prêt au dépôt',
};

/** Le document généré par cette préparation, quel que soit le nom du champ.
 *  docx.document_id est la forme confirmée par le serveur (bloc docx de
 *  GET /cv-prep/:id) — les autres restent en tolérance de lecture. */
const docsDePrep = (p) => [
  p.docx?.document_id, p.docx?.id,
  p.document_id, p.doc_id, p.cv_document_id, p.generated_document_id,
  p.document?.id, p.cv_document?.id,
].filter(v => v != null).map(String);

/**
 * État de préparation des sections, publié par GET /cv-prep/:id.
 * 'pret' = éditable ; 'en_cours' = le serveur prépare encore ; toute autre
 * valeur = refus/échec, expliqué par etat_message. Champ absent (réponse
 * d'avant le contrat) = on ouvre comme avant.
 */
const etatEditeurDe = (p) => p?.etat_editeur ?? p?.etatEditeur ?? null;
const messageEtatDe = (p) => p?.etat_message ?? p?.etatMessage ?? null;

/** Date de dernière modification, pour départager les doublons d'un besoin. */
const dateDe = (p) => {
  const d = p.updated_at || p.updatedAt || p.created_at || p.createdAt;
  const t = d ? Date.parse(d) : NaN;
  return Number.isNaN(t) ? -Infinity : t;
};

/**
 * Une seule préparation par besoin : la plus récente.
 *
 * La règle métier est « une seule et dernière version de CV par besoin » — le
 * serveur la garantira à la création. En attendant, les doublons historiques
 * (optimisations relancées) ne doivent pas transformer « Éditer » en liste de
 * versions périmées : on ne montre et n'ouvre que la dernière de chaque
 * besoin, les anciennes n'étant plus jamais proposées.
 */
const uneParBesoin = (liste) => {
  const parCle = new Map();
  for (const p of liste) {
    const cle = String(p.besoin_id ?? p.besoinId ?? 'libre');
    const t = parCle.get(cle);
    if (!t || dateDe(p) > dateDe(t) || (dateDe(p) === dateDe(t) && Number(p.id ?? 0) > Number(t.id ?? 0))) parCle.set(cle, p);
  }
  return [...parCle.values()].sort((a, b) => dateDe(b) - dateDe(a));
};

/**
 * Un contenu structuré « fantôme » : rien qui ressemble à un vrai CV.
 * (Brouillons de test, créations vierges abandonnées — aucune expérience,
 * aucune formation, et quelques caractères de texte au mieux. Le nom est
 * ignoré : il est pré-rempli depuis la fiche même dans un brouillon vide.)
 */
const quasiVide = (cv) => {
  if (!cv) return true;
  const exps = (Array.isArray(cv.experience) ? cv.experience : [])
    .filter(e => e?.position || e?.company || (Array.isArray(e?.tasks) && e.tasks.some(t => String(t || '').trim())));
  const edu = (Array.isArray(cv.education) ? cv.education : []).filter(e => e && Object.values(e).some(v => String(v || '').trim()));
  const texte = [cv.profile, cv.title, ...(Array.isArray(cv.skills?.technical_skills) ? cv.skills.technical_skills : [])]
    .filter(Boolean).join(' ');
  return exps.length === 0 && edu.length === 0 && texte.trim().length < 120;
};

/** Le CV déposé du candidat, quand l'écran appelant ne précise pas le document.
 *  Même heuristique que l'aperçu de la fiche : document principal, sinon
 *  type/label « cv », sinon le premier. */
const cvDepose = (cand) => {
  const docs = Array.isArray(cand?.documents) ? cand.documents : [];
  return docs.find(d => d.is_principal)
    || docs.find(d => /^cv/i.test(d.type_piece || '') || /\bcv\b/i.test(d.label || ''))
    || docs[0] || null;
};

/**
 * POST /cv-prep, partagé entre l'ouverture automatique et les boutons du repli.
 * Renvoie { id } en succès ; { refus } quand la route manque ou refuse le mode
 * (rien n'est créé) ; { erreur } sur échec franc.
 */
async function creerPreparation({ candidateId, besoinId, mode, documentId }) {
  const corps = { candidate_id: candidateId, mode };
  if (besoinId) corps.besoin_id = Number(besoinId) || besoinId;
  if (mode === 'import') corps.document_id = documentId;
  const r = await fetch(`${BASE}/cv-prep`, {
    method: 'POST',
    headers: { ...authH(), 'Content-Type': 'application/json' },
    body: JSON.stringify(corps),
  });
  const j = await r.json().catch(() => ({}));
  const refusBesoin = r.status === 400 && !besoinId && /besoin/i.test(j.error || '');
  if (r.status === 404 || r.status === 501 || refusBesoin || (r.status === 400 && /mode|document_id/i.test(j.error || ''))) {
    return { refus: refusBesoin ? 'besoin' : 'mode' };
  }
  if (!r.ok || j.success === false || j.id == null) return { erreur: j.error || `HTTP ${r.status}` };
  return { id: j.id };
}

const MSG_REFUS = {
  besoin: "L'édition sans besoin arrive côté serveur — rien n'a été créé. En attendant, choisissez un besoin ci-dessus.",
  import: 'La reprise du contenu du fichier arrive côté serveur — rien n’a été créé. En attendant, le CV vierge reste disponible.',
  vierge: 'La création d’un CV vierge arrive côté serveur — rien n’a été créé.',
};

export default function CvEditeurLauncher({ candidateId, besoinId, docId, candidatNom, showNotification, onClose, zIndex = 700 }) {
  const [etat, setEtat] = useState('chargement');  // chargement | ouverture | choix | edition | aucun | erreur
  const [preps, setPreps] = useState([]);
  const [prepId, setPrepId] = useState(null);
  const [prep, setPrep] = useState(null);
  const [erreur, setErreur] = useState(null);
  // Sections pas encore prêtes : ce que dit le serveur (etat_editeur/etat_message),
  // affiché à la place du canevas vide qui faisait croire à un CV perdu.
  const [attente, setAttente] = useState(null);   // { etat, message } | null
  // Écran d'optimisation ouvert sur place : il porte le choix du besoin, le
  // lancement, le suivi, puis l'édition — inutile d'en réécrire une version.
  const [optimiser, setOptimiser] = useState(false);
  const [positions, setPositions] = useState(null);
  const [besoinCible, setBesoinCible] = useState(besoinId != null ? String(besoinId) : '');
  const [creation, setCreation] = useState(null);      // 'import' | 'vierge' | null
  const [creationRefus, setCreationRefus] = useState(null);
  // Le document déposé à reprendre : celui de l'écran appelant, sinon celui
  // retrouvé sur la fiche. null = rien à reprendre (vrai départ de zéro).
  const [docImport, setDocImport] = useState(docId ?? null);
  const autoTente = useRef(false); // une seule ouverture automatique par montage

  const chargerPrep = useCallback(async (id) => {
    const r = await fetch(`${BASE}/cv-prep/${id}`, { headers: authH() });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const j = await r.json().catch(() => ({}));
    return j.data || j;
  }, []);

  const ouvrir = useCallback(async (id, opts) => {
    setEtat('chargement');
    try {
      const d = await chargerPrep(id);
      // On édite ce qu'on a sous les yeux : si la version modifiable trouvée
      // est un brouillon fantôme (rien d'un vrai CV) alors qu'on regarde un
      // document plein qui ne vient pas d'elle, on reprend CE document —
      // jamais un vieux brouillon de test à la place du CV affiché.
      if (opts?.remplacerSiVide && docId != null && !autoTente.current
        && quasiVide(d?.structured_cv) && !docsDePrep(d).includes(String(docId))) {
        autoTente.current = true;
        setEtat('ouverture');
        const res = await creerPreparation({
          candidateId,
          besoinId: besoinId ?? (d.besoin_id ?? d.besoinId),
          mode: 'import', documentId: docId,
        });
        if (res.id != null) {
          const d2 = await chargerPrep(res.id);
          setPrep(d2); setPrepId(res.id); setEtat('edition');
          return;
        }
        // Reprise impossible (route absente, fichier illisible) : on ouvre
        // quand même le brouillon existant plutôt que de bloquer.
      }
      const et = etatEditeurDe(d);
      if (et && et !== 'pret') {
        // Ni canevas vide ni page blanche : l'attente (ou le refus) se lit.
        setPrep(d); setPrepId(id);
        setAttente({ etat: et, message: messageEtatDe(d) });
        setEtat('attente');
        return;
      }
      setPrep(d); setPrepId(id); setAttente(null); setEtat('edition');
    } catch (e) { setErreur(e.message); setEtat('erreur'); }
  }, [chargerPrep, candidateId, besoinId, docId]);

  useEffect(() => {
    let annule = false;
    (async () => {
      if (candidateId == null) { setEtat('aucun'); return; }
      try {
        const r = await fetch(`${BASE}/cv-prep?candidate_id=${candidateId}`, { headers: authH() });
        if (r.status === 404 || r.status === 501) { if (!annule) setEtat('aucun'); return; }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json().catch(() => ({}));
        const liste = j.data || j.preparations || (Array.isArray(j) ? j : []);
        if (annule) return;
        setPreps(liste);
        if (!liste.length) {
          // Un CV déposé existe ? On l'ouvre directement : sa version
          // modifiable est créée par reprise fidèle du contenu, sans écran
          // intermédiaire — comme on ouvre un document dans Word. L'écran
          // manuel ne sert que de repli (reprise impossible, aucun fichier).
          if (!autoTente.current) {
            autoTente.current = true;
            let idDoc = docId ?? null;
            if (idDoc == null) {
              try {
                const rv = await fetch(`${BASE}/candidats/vue/${candidateId}`, { headers: authH() });
                if (rv.ok) {
                  const jv = await rv.json();
                  const cand = jv.data || jv.candidat || jv;
                  idDoc = cvDepose(cand)?.id ?? null;
                  const pos = cand?.positionnements || cand?.besoins || jv.positionnements;
                  if (Array.isArray(pos) && !annule) setPositions(pos);
                }
              } catch (e) { /* fiche illisible : on passe à l'écran manuel */ }
            }
            if (annule) return;
            if (idDoc != null) {
              setDocImport(idDoc);
              setEtat('ouverture');
              const res = await creerPreparation({ candidateId, besoinId, mode: 'import', documentId: idDoc });
              if (annule) return;
              if (res.id != null) { await ouvrir(res.id); return; }
              // Rien n'a été créé (route absente) ou la reprise a échoué : on
              // le dit sur l'écran manuel, qui garde toutes les autres voies.
              setCreationRefus(res.refus
                ? (res.refus === 'besoin' ? MSG_REFUS.besoin : MSG_REFUS.import)
                : `La reprise automatique n'a pas abouti : ${res.erreur}`);
            }
          }
          setEtat('aucun');
          return;
        }

        // Une seule version par besoin : la plus récente. Le doc regardé prime
        // (il désigne une préparation précise) ; sinon on prend la dernière du
        // besoin en cours, ou la seule qui reste après dédoublonnage.
        const recentes = uneParBesoin(liste);
        const parDoc = docId != null && liste.find(p => docsDePrep(p).includes(String(docId)));
        const parBesoin = besoinId != null && recentes.find(p => String(p.besoin_id ?? p.besoinId) === String(besoinId));
        const cible = parDoc || parBesoin || (recentes.length === 1 ? recentes[0] : null);
        // remplacerSiVide : ne vaut que pour cette résolution automatique —
        // un choix explicite de l'utilisateur ouvre toujours ce qu'il a choisi.
        if (cible) ouvrir(cible.id, { remplacerSiVide: !parDoc }); else setEtat('choix');
      } catch (e) { if (!annule) { setErreur(e.message); setEtat('erreur'); } }
    })();
    return () => { annule = true; };
  }, [candidateId, besoinId, docId, ouvrir]);

  // Besoins du candidat, pour le choix de la cible d'optimisation. Deux routes,
  // comme sur la fiche : la vue candidat porte les positionnements, la seconde
  // sert de repli.
  useEffect(() => {
    if (etat !== 'aucun' || candidateId == null || positions != null) return;
    let annule = false;
    (async () => {
      const essais = [
        [`${BASE}/candidats/vue/${candidateId}`, (j) => {
          const cand = j.data || j.candidat || j;
          return cand?.positionnements || cand?.besoins || j.positionnements;
        }],
        [`${BASE}/candidates/${candidateId}/besoins`, (j) => j.besoins || j.positionnements || j.data || (Array.isArray(j) ? j : [])],
      ];
      for (const [url, extraire] of essais) {
        try {
          const r = await fetch(url, { headers: authH() });
          if (!r.ok) continue;
          const arr = extraire(await r.json());
          if (Array.isArray(arr)) { if (!annule) setPositions(arr); return; }
        } catch (e) { /* on tente la route suivante */ }
      }
      if (!annule) setPositions([]);
    })();
    return () => { annule = true; };
  }, [etat, candidateId, positions]);

  const besoinsOptions = (positions || [])
    .map(p => ({
      id: p.besoin_id ?? p.besoinId ?? p.id,
      titre: p.besoin_titre ?? p.titre ?? p.poste ?? `Besoin ${p.besoin_id ?? p.id}`,
      client: p.client || p.besoin_client,
    }))
    .filter(o => o.id != null);

  // Un seul besoin : il n'y a rien à choisir, on le pré-sélectionne.
  useEffect(() => {
    if (!besoinCible && besoinsOptions.length === 1) setBesoinCible(String(besoinsOptions[0].id));
  }, [besoinCible, besoinsOptions]);

  /**
   * Crée la version modifiable : `import` reprend le contenu du fichier déposé
   * tel quel, `vierge` part d'une page vide — dans les deux cas sans traitement
   * IA, et sans exiger de besoin (facultatif, rattachable plus tard).
   *
   * La route n'est pas encore servie partout : un refus (404, 501, ou un 400
   * qui dit ne pas connaître ce mode / exiger un besoin) est annoncé et NE crée
   * rien, plutôt que de retomber silencieusement sur l'optimisation, qui elle
   * coûte et réécrit.
   */
  const creer = async (mode) => {
    setCreation(mode); setCreationRefus(null);
    try {
      const res = await creerPreparation({ candidateId, besoinId: besoinCible, mode, documentId: docImport });
      if (res.refus) {
        setCreationRefus(res.refus === 'besoin' ? MSG_REFUS.besoin : MSG_REFUS[mode]);
        return;
      }
      if (res.id == null) throw new Error(res.erreur);
      showNotification?.(mode === 'import' ? 'Contenu du CV repris — modifiez-le librement.' : 'CV vierge créé — à vous de le remplir.', 'success');
      await ouvrir(res.id);
    } catch (e) { showNotification?.(`Échec : ${e.message}`, 'error'); }
    finally { setCreation(null); }
  };

  // Tant que le serveur prépare les sections (etat_editeur === 'en_cours'), on
  // le réinterroge : l'éditeur s'ouvre de lui-même dès que c'est prêt, sans
  // avoir à fermer et rouvrir la fenêtre.
  useEffect(() => {
    if (etat !== 'attente' || attente?.etat !== 'en_cours' || prepId == null) return undefined;
    let annule = false;
    const t = setInterval(async () => {
      try {
        const d = await chargerPrep(prepId);
        if (annule) return;
        setPrep(d);
        const et = etatEditeurDe(d);
        if (!et || et === 'pret') { setAttente(null); setEtat('edition'); }
        else setAttente({ etat: et, message: messageEtatDe(d) });
      } catch (e) { /* réseau : on retente au tour suivant */ }
    }, 2500);
    return () => { annule = true; clearInterval(t); };
  }, [etat, attente?.etat, prepId, chargerPrep]);

  const recharger = useCallback(async () => {
    if (prepId == null) return;
    try {
      const d = await chargerPrep(prepId);
      setPrep(d);
      // Une régénération peut remettre les sections en préparation : on le dit,
      // au lieu de laisser l'éditeur afficher un contenu qui n'est plus à jour.
      const et = etatEditeurDe(d);
      if (et && et !== 'pret') { setAttente({ etat: et, message: messageEtatDe(d) }); setEtat('attente'); }
    } catch (e) { /* l'éditeur garde son état */ }
  }, [prepId, chargerPrep]);

  const cible = preps.find(p => p.id === prepId);
  const sousTitre = cible
    ? `Besoin « ${cible.besoin_titre || cible.poste || cible.besoin_id || '—'} »`
    : null;

  // L'écran d'optimisation existe déjà et fait tout le chemin : choix du
  // besoin, lancement, suivi, comparaison, puis « Éditer le CV ».
  if (optimiser) {
    return (
      <CVPrep candidateId={candidateId} besoinId={besoinId} positions={positions || []}
        showNotification={showNotification} onClose={onClose} />
    );
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.62)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex, padding: '24px', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--panel, #fff)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '1180px', height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderBottom: '1px solid var(--hair)', flexShrink: 0 }}>
          <BI icon="pencil-square" style={{ color: 'var(--brand)', flex: 'none' }} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: 'var(--text-strong)' }}>
              Éditer le CV{candidatNom ? ` — ${candidatNom}` : ''}
            </span>
            {sousTitre && <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--text-2)' }}>{sousTitre}</span>}
          </span>
          <button onClick={onClose} style={gBtn} title="Fermer"><BI icon="x-lg" /></button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: etat === 'edition' ? '0 16px 16px' : '28px 20px' }}>

          {(etat === 'chargement' || etat === 'ouverture') && (
            <div style={{ textAlign: 'center', color: 'var(--text-faint)', padding: '40px 0' }}>
              <BI icon="arrow-repeat" style={{ fontSize: '22px', animation: 'cvl-spin .7s linear infinite' }} />
              {etat === 'ouverture' && (
                <div style={{ marginTop: '10px', fontSize: '12.5px', color: 'var(--text-2)' }}>
                  Ouverture du CV — son contenu est repris tel quel, prêt à être modifié.
                </div>
              )}
              <style>{`@keyframes cvl-spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          )}

          {/* Sections pas encore prêtes : l'attente ou le refus du serveur, mot
              pour mot — jamais un canevas vide. */}
          {etat === 'attente' && (
            <div style={{ maxWidth: '520px', margin: '0 auto', textAlign: 'center', color: 'var(--text-2)' }}>
              <style>{`@keyframes cvl-spin{to{transform:rotate(360deg)}}`}</style>
              {attente?.etat === 'en_cours' ? (
                <>
                  <BI icon="arrow-repeat" style={{ fontSize: '22px', color: 'var(--text-faint)', animation: 'cvl-spin .7s linear infinite' }} />
                  <div style={{ marginTop: '12px', fontSize: '14px', fontWeight: 600, color: 'var(--text-strong)' }}>Préparation du CV en cours</div>
                </>
              ) : (
                <>
                  <BI icon="exclamation-triangle" style={{ fontSize: '24px', color: 'var(--erp-warning)' }} />
                  <div style={{ marginTop: '12px', fontSize: '14px', fontWeight: 600, color: 'var(--text-strong)' }}>Ce CV n'est pas encore modifiable</div>
                </>
              )}
              <p style={{ marginTop: '8px', fontSize: '12.5px', lineHeight: 1.6 }}>
                {attente?.message || (attente?.etat === 'en_cours'
                  ? "Les sections modifiables sont en cours de préparation — l'éditeur s'ouvrira tout seul."
                  : "Le serveur n'a pas donné de détail.")}
              </p>
              {attente?.etat !== 'en_cours' && (
                <button onClick={() => ouvrir(prepId)} style={{ ...gBtn, marginTop: '12px' }}>
                  <BI icon="arrow-repeat" /> Réessayer
                </button>
              )}
            </div>
          )}

          {etat === 'erreur' && (
            <div style={{ textAlign: 'center', color: 'var(--text-2)', padding: '20px' }}>
              <BI icon="exclamation-triangle" style={{ fontSize: '24px', color: '#d97706' }} />
              <div style={{ marginTop: '8px', fontSize: '13.5px', fontWeight: 600 }}>Impossible d'ouvrir l'éditeur</div>
              {erreur && <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--text-3)' }}>{erreur}</div>}
            </div>
          )}

          {/* Rien à éditer encore. On crée la version modifiable ici, à la main :
              contenu du fichier repris tel quel, ou page vide — sans IA, et sans
              exiger de besoin. */}
          {etat === 'aucun' && (
            <div style={{ maxWidth: '540px', margin: '0 auto', color: 'var(--text-2)' }}>
              <div style={{ textAlign: 'center' }}>
                <BI icon="file-earmark-text" style={{ fontSize: '26px', color: 'var(--text-faint)' }} />
                <div style={{ marginTop: '10px', fontSize: '14px', fontWeight: 600, color: 'var(--text-strong)' }}>
                  Ce CV n'a pas encore de version modifiable
                </div>
                <p style={{ marginTop: '8px', fontSize: '12.5px', lineHeight: 1.6 }}>
                  Un CV déposé (PDF ou Word) est un fichier : pour l'éditer mot par mot, on en fait
                  une version structurée — en reprenant son contenu tel quel, ou en partant d'une page vide.
                </p>
              </div>

              <div style={{ marginTop: '18px', border: '1px solid var(--border)', borderRadius: '11px', padding: '15px' }}>
                <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '5px' }}>Besoin concerné (facultatif)</label>
                <ErpiaSelect value={besoinCible} onChange={e => setBesoinCible(e.target.value)} disabled={positions == null}
                  style={{ width: '100%', padding: '9px 11px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '13px', outline: 'none' }}>
                  <option value="">{positions == null ? 'Chargement…' : '— Aucun : édition libre —'}</option>
                  {besoinsOptions.map(o => <option key={o.id} value={o.id}>{o.titre}{o.client ? ` · ${o.client}` : ''}</option>)}
                </ErpiaSelect>
                <p style={{ marginTop: '5px', marginBottom: 0, fontSize: '11px', color: 'var(--text-3)', lineHeight: 1.5 }}>
                  Sans besoin, pas de score ni d'optimisation ciblée — vous pourrez en rattacher un plus tard.
                </p>

                {docImport != null && (
                  <>
                    <button onClick={() => creer('import')} disabled={creation != null}
                      style={{ marginTop: '12px', width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', padding: '10px 16px', borderRadius: '9px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '13px', fontWeight: 700, cursor: creation != null ? 'not-allowed' : 'pointer', opacity: creation != null ? 0.55 : 1 }}>
                      <BI icon={creation === 'import' ? 'arrow-repeat' : 'file-earmark-arrow-down'} style={creation === 'import' ? { animation: 'cvl-spin .8s linear infinite' } : undefined} />
                      {creation === 'import' ? 'Reprise du contenu…' : 'Reprendre le contenu du CV déposé et l’éditer'}
                    </button>
                    <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>
                      Le texte du fichier est repris tel quel, section par section — rien n'est réécrit.
                    </p>
                  </>
                )}

                <button onClick={() => creer('vierge')} disabled={creation != null}
                  style={{ marginTop: '12px', width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', padding: '10px 16px', borderRadius: '9px', border: docImport != null ? '1px solid var(--input-border)' : 'none', background: docImport != null ? 'var(--ghost-bg)' : 'var(--accent)', color: docImport != null ? 'var(--text)' : 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '13px', fontWeight: 700, cursor: creation != null ? 'not-allowed' : 'pointer', opacity: creation != null ? 0.55 : 1 }}>
                  <BI icon={creation === 'vierge' ? 'arrow-repeat' : 'pencil-square'} style={creation === 'vierge' ? { animation: 'cvl-spin .8s linear infinite' } : undefined} />
                  {creation === 'vierge' ? 'Création…' : 'Créer un CV vierge et l’éditer'}
                </button>
                <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>
                  Aucun traitement automatique : vous obtenez un CV vide, à remplir vous-même.
                </p>

                {creationRefus && (
                  <div style={{ marginTop: '12px', padding: '10px 12px', borderRadius: '9px', background: 'var(--chip-bg)', fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
                    {creationRefus}
                  </div>
                )}

                <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--hair)' }}>
                  <button onClick={() => setOptimiser(true)} disabled={positions == null}
                    style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-2)', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
                    Ou pré-remplir depuis le CV déposé
                  </button>
                  <p style={{ marginTop: '5px', marginBottom: 0, fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>
                    Reprend le CV du candidat et le met au format entreprise (besoin requis). Le résultat s'édite de la même façon.
                  </p>
                </div>
              </div>
            </div>
          )}

          {etat === 'choix' && (
            <div style={{ maxWidth: '560px', margin: '0 auto' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)', marginBottom: '4px' }}>Quel CV modifier ?</div>
              <p style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: 0, marginBottom: '14px' }}>
                Ce candidat a un CV préparé pour plusieurs besoins — une seule version par besoin, la dernière.
                Ils sont indépendants : modifier l'un ne touche pas les autres.
              </p>
              {uneParBesoin(preps).map(p => (
                <button key={p.id} onClick={() => ouvrir(p.id)}
                  style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 13px', border: '1px solid var(--border)', borderRadius: '9px', background: 'var(--panel)', cursor: 'pointer', marginBottom: '7px', fontFamily: 'inherit' }}>
                  <BI icon="file-earmark-text" style={{ color: 'var(--brand)', flex: 'none' }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>
                      {p.besoin_titre || p.poste || ((p.besoin_id ?? p.besoinId) != null ? `Besoin ${p.besoin_id ?? p.besoinId}` : 'Édition libre (sans besoin)')}
                    </span>
                    {p.client && <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--text-2)' }}>{p.client}</span>}
                  </span>
                  <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '6px', background: 'var(--chip-bg)', color: 'var(--text-2)', flex: 'none' }}>
                    {STATUTS[p.status] || 'Brouillon'}
                  </span>
                </button>
              ))}
            </div>
          )}

          {etat === 'edition' && prep && (
            <CvEditeur prep={prep} prepId={prepId} candidatNom={candidatNom || prep.structured_cv?.name || ''}
              showNotification={showNotification} onReload={recharger} onClose={onClose} />
          )}
        </div>
      </div>
    </div>
  );
}
